import { supabase } from '../lib/supabase';

// ============================================
// REAL-TIME SUBSCRIPTIONS SERVICE
// ============================================

/**
 * Subscribe to market value changes
 * @param {Function} callback - Callback function to handle market value updates
 * @returns {Object} Supabase channel object
 */
export const subscribeToMarketValueChanges = (callback) => {
  const channel = supabase
    ?.channel('market_value_changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'market_value_history'
      },
      (payload) => {
        console.log('Market value change detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

/**
 * Subscribe to transfer alerts
 * @param {Function} callback - Callback function to handle transfer updates
 * @returns {Object} Supabase channel object
 */
export const subscribeToTransferAlerts = (callback) => {
  const channel = supabase
    ?.channel('transfer_alerts')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'transfer_history'
      },
      (payload) => {
        console.log('Transfer alert detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

/**
 * Subscribe to player profile changes (for trending rankings)
 * @param {Function} callback - Callback function to handle player profile updates
 * @returns {Object} Supabase channel object
 */
export const subscribeToPlayerProfileChanges = (callback) => {
  const channel = supabase
    ?.channel('player_profile_changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'player_master_profiles'
      },
      (payload) => {
        console.log('Player profile change detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

/**
 * Subscribe to all real-time updates (market value, transfers, player profiles)
 * @param {Object} callbacks - Object containing callback functions for each event type
 * @param {Function} callbacks.onMarketValueChange - Callback for market value changes
 * @param {Function} callbacks.onTransferAlert - Callback for transfer alerts
 * @param {Function} callbacks.onPlayerProfileChange - Callback for player profile changes
 * @returns {Array} Array of Supabase channel objects
 */
export const subscribeToAllRealtimeUpdates = (callbacks) => {
  const channels = [];

  if (callbacks?.onMarketValueChange) {
    channels?.push(subscribeToMarketValueChanges(callbacks?.onMarketValueChange));
  }

  if (callbacks?.onTransferAlert) {
    channels?.push(subscribeToTransferAlerts(callbacks?.onTransferAlert));
  }

  if (callbacks?.onPlayerProfileChange) {
    channels?.push(subscribeToPlayerProfileChanges(callbacks?.onPlayerProfileChange));
  }

  return channels;
};

/**
 * Unsubscribe from a single channel
 * @param {Object} channel - Supabase channel object to unsubscribe
 */
export const unsubscribeFromChannel = (channel) => {
  if (channel) {
    supabase?.removeChannel(channel);
  }
};

/**
 * Unsubscribe from multiple channels
 * @param {Array} channels - Array of Supabase channel objects to unsubscribe
 */
export const unsubscribeFromAllChannels = (channels) => {
  if (channels && Array.isArray(channels)) {
    channels?.forEach(channel => {
      if (channel) {
        supabase?.removeChannel(channel);
      }
    });
  }
};

// ============================================
// DATA FETCHING SERVICES
// ============================================

/**
 * Fetch recent market value changes
 * @param {Number} days - Number of days to look back (default: 30)
 * @returns {Promise} Promise resolving to market value changes data
 */
export const fetchRecentMarketValueChanges = async (days = 30) => {
  try {
    const cutoffDate = new Date();
    cutoffDate?.setDate(cutoffDate?.getDate() - days);
    const cutoffDateStr = cutoffDate?.toISOString()?.split('T')?.[0];

    const { data, error } = await supabase
      ?.from('market_value_history')
      ?.select(`
        id,
        player_id,
        valuation_date,
        market_value,
        currency,
        created_at,
        player_master_profiles (
          id,
          full_name,
          known_as,
          primary_position,
          current_club,
          current_league,
          profile_photo
        )
      `)
      ?.gte('valuation_date', cutoffDateStr)
      ?.order('valuation_date', { ascending: false })
      ?.limit(50);

    if (error) throw error;

    // Convert to camelCase
    const camelCaseData = data?.map(item => ({
      id: item?.id,
      playerId: item?.player_id,
      valuationDate: item?.valuation_date,
      marketValue: item?.market_value,
      currency: item?.currency,
      createdAt: item?.created_at,
      player: item?.player_master_profiles ? {
        id: item?.player_master_profiles?.id,
        fullName: item?.player_master_profiles?.full_name,
        knownAs: item?.player_master_profiles?.known_as,
        primaryPosition: item?.player_master_profiles?.primary_position,
        currentClub: item?.player_master_profiles?.current_club,
        currentLeague: item?.player_master_profiles?.current_league,
        profilePhoto: item?.player_master_profiles?.profile_photo
      } : null
    }));

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching market value changes:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Fetch recent transfer alerts
 * @param {Number} days - Number of days to look back (default: 90)
 * @returns {Promise} Promise resolving to transfer alerts data
 */
export const fetchRecentTransferAlerts = async (days = 90) => {
  try {
    const cutoffDate = new Date();
    cutoffDate?.setDate(cutoffDate?.getDate() - days);
    const cutoffDateStr = cutoffDate?.toISOString()?.split('T')?.[0];

    const { data, error } = await supabase
      ?.from('transfer_history')
      ?.select(`
        id,
        player_id,
        from_club,
        to_club,
        transfer_date,
        transfer_fee,
        transfer_type,
        currency,
        created_at,
        player_master_profiles (
          id,
          full_name,
          known_as,
          primary_position,
          current_club,
          profile_photo
        )
      `)
      ?.gte('transfer_date', cutoffDateStr)
      ?.order('transfer_date', { ascending: false })
      ?.limit(50);

    if (error) throw error;

    // Convert to camelCase
    const camelCaseData = data?.map(item => ({
      id: item?.id,
      playerId: item?.player_id,
      fromClub: item?.from_club,
      toClub: item?.to_club,
      transferDate: item?.transfer_date,
      transferFee: item?.transfer_fee,
      transferType: item?.transfer_type,
      currency: item?.currency,
      createdAt: item?.created_at,
      player: item?.player_master_profiles ? {
        id: item?.player_master_profiles?.id,
        fullName: item?.player_master_profiles?.full_name,
        knownAs: item?.player_master_profiles?.known_as,
        primaryPosition: item?.player_master_profiles?.primary_position,
        currentClub: item?.player_master_profiles?.current_club,
        profilePhoto: item?.player_master_profiles?.profile_photo
      } : null
    }));

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching transfer alerts:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Fetch trending talent rankings
 * @param {Number} limit - Number of results to return (default: 20)
 * @returns {Promise} Promise resolving to trending talent data
 */
export const fetchTrendingTalentRankings = async (limit = 20) => {
  try {
    const { data, error } = await supabase
      ?.from('trending_talent_rankings')
      ?.select('*')
      ?.order('trending_score', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    // Convert to camelCase
    const camelCaseData = data?.map(item => ({
      id: item?.id,
      fullName: item?.full_name,
      knownAs: item?.known_as,
      primaryPosition: item?.primary_position,
      currentClub: item?.current_club,
      currentLeague: item?.current_league,
      currentCountry: item?.current_country,
      nationalityPrimary: item?.nationality_primary,
      currentMarketValue: item?.current_market_value,
      highestMarketValue: item?.highest_market_value,
      currency: item?.currency,
      profilePhoto: item?.profile_photo,
      dateOfBirth: item?.date_of_birth,
      availableForTransfer: item?.available_for_transfer,
      availableForLoan: item?.available_for_loan,
      age: item?.age,
      marketValueChange30d: item?.market_value_change_30d,
      recentTransfersCount: item?.recent_transfers_count,
      totalGoals: item?.total_goals,
      totalAssists: item?.total_assists,
      totalAppearances: item?.total_appearances,
      trendingScore: item?.trending_score,
      updatedAt: item?.updated_at
    }));

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching trending talent rankings:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Refresh trending talent rankings materialized view
 * @returns {Promise} Promise resolving to refresh result
 */
export const refreshTrendingRankings = async () => {
  try {
    const { data, error } = await supabase?.rpc('refresh_trending_rankings');
    
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error refreshing trending rankings:', error);
    return { success: false, error: error?.message };
  }
};